import { Component, OnInit, TemplateRef } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { BenutzermanagementAPIService } from '../../api/benutzermanagement/benutzermanagement-api.service';
import {
  ManagedAccount,
  MatchingPin,
  MatchingPinStatus,
} from '../../api/benutzermanagement/models';
import { AppToastService } from '../../toast/app-toast.service';
import {
  faCheck,
  faCopy,
  faInfoCircle,
  faSpinner,
  faTimes,
  faTrashAlt,
} from '@fortawesome/pro-solid-svg-icons';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import {
  BenutzerInfoReducerState,
  getBenutzerInfoFeatureState,
} from '../../reducers/authinfo.reducer';
import { ManagedAccountService } from '../../api/managed-account/managed-account.service';

interface ModalRefLike {
  close: (reason?: unknown) => void;
  dismiss: (reason?: unknown) => void;
}

@Component({
  selector: 'san-verwalter-accounts',
  templateUrl: './verwalter-accounts.component.html',
  styleUrls: ['./verwalter-accounts.component.scss'],
  standalone: false,
})
export class VerwalterAccountsComponent implements OnInit {
  // Icons
  loadingIcon = faSpinner;
  infoIcon = faInfoCircle;
  deleteIcon = faTrashAlt;
  copyIcon = faCopy;
  checkIcon = faCheck;
  timesIcon = faTimes;

  // Forms
  generatePinForm: FormGroup;
  enterPinForm: FormGroup;

  // Data
  managedAccounts: ManagedAccount[] = [];
  managerAccounts: ManagedAccount[] = [];
  matchingPins: MatchingPin[] = [];
  currentPin: MatchingPin | null = null;
  pendingConfirmation: MatchingPin | null = null;
  selectedPinForActivation: MatchingPin | null = null;
  selectedPinForRejection: MatchingPin | null = null;
  selectedPinForTermination: MatchingPin | null = null;
  selectedManagedAccountForTermination: ManagedAccount | null = null;
  selectedManagerAccountForTermination: ManagedAccount | null = null;

  // State
  isLoading = false;
  isPinGenerated = false;
  isPinEntered = false;
  showConfirmation = false;
  currentUsername = '';

  // expose enum to template
  MatchingPinStatus = MatchingPinStatus;

  constructor(
    private benutzermanagementApi: BenutzermanagementAPIService,
    private formBuilder: FormBuilder,
    private toastService: AppToastService,
    private modalService: NgbModal,
    private store: Store<BenutzerInfoReducerState>,
    private managedAccountService: ManagedAccountService,
  ) {
    this.generatePinForm = this.formBuilder.group({});
    this.enterPinForm = this.formBuilder.group({
      pin: [
        '',
        [
          (control: AbstractControl) => Validators.required(control),
          (control: AbstractControl) => Validators.minLength(9)(control),
        ],
      ],
    });
  }

  ngOnInit(): void {
    this.store.select(getBenutzerInfoFeatureState).subscribe((userInfo) => {
      if (!userInfo) {
        return;
      }
      this.currentUsername = userInfo.auth.username || '';
    });

    this.isLoading = true;

    // Load managed accounts, manager accounts, and matching pins
    this.loadAllAccountData();
  }

  loadManagedAccounts(): void {
    this.isLoading = true;
    this.benutzermanagementApi.getManagedAccounts().subscribe(
      (accounts) => {
        this.managedAccounts = accounts;
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading managed accounts', error);
        this.toastService.show(
          'Fehler',
          'Fehler beim Laden der verwalteten Konten',
          'danger',
        );
        this.isLoading = false;
      },
    );
  }

  loadAllAccountData(): void {
    this.isLoading = true;

    // Load managed accounts
    this.benutzermanagementApi.getManagedAccounts().subscribe(
      (accounts) => {
        this.managedAccounts = accounts;

        // Load manager accounts after managed accounts
        this.benutzermanagementApi.getManagerAccounts().subscribe(
          (managers) => {
            this.managerAccounts = managers;

            // Now load matching pins
            this.benutzermanagementApi.getMatchingPins().subscribe(
              (pins) => {
                this.matchingPins = pins;
                this.isLoading = false;
                this.managedAccountService.triggerReload();
              },
              (error) => {
                console.error('Error loading matching pins', error);
                this.toastService.show(
                  'Fehler',
                  'Fehler beim Laden der Matching-PINs',
                  'danger',
                );
                this.isLoading = false;
              },
            );
          },
          (error) => {
            console.error('Error loading manager accounts', error);
            this.toastService.show(
              'Fehler',
              'Fehler beim Laden der verwaltenden Konten',
              'danger',
            );
            this.isLoading = false;
          },
        );
      },
      (error) => {
        console.error('Error loading managed accounts', error);
        this.toastService.show(
          'Fehler',
          'Fehler beim Laden der verwalteten Konten',
          'danger',
        );
        this.isLoading = false;
      },
    );
  }

  loadMatchingPins(): void {
    this.isLoading = true;
    this.benutzermanagementApi.getMatchingPins().subscribe(
      (pins) => {
        this.matchingPins = pins;
        this.isLoading = false;
        this.managedAccountService.triggerReload();
      },
      (error) => {
        console.error('Error loading matching pins', error);
        this.toastService.show(
          'Fehler',
          'Fehler beim Laden der Matching-PINs',
          'danger',
        );
        this.isLoading = false;
      },
    );
  }

  deletePendingPin(pin: MatchingPin): void {
    if (!pin || !pin.id) {
      return;
    }
    this.isLoading = true;
    this.benutzermanagementApi.deleteUnusedMatchingPin(pin.id).subscribe(
      () => {
        this.toastService.show(
          'Erfolg',
          'Unbenutzte PIN wurde gelöscht',
          'success',
        );
        this.benutzermanagementApi.getMatchingPins().subscribe(
          (pins) => {
            this.matchingPins = pins;
            this.isLoading = false;
            this.managedAccountService.triggerReload();
          },
          (error) => {
            console.error('Error loading matching pins', error);
            this.toastService.show(
              'Fehler',
              'Fehler beim Laden der Matching-PINs',
              'danger',
            );
            this.isLoading = false;
          },
        );
      },
      (error) => {
        console.error('Error deleting pending PIN', error);
        this.toastService.show(
          'Fehler',
          'Fehler beim Löschen der unbenutzten PIN',
          'danger',
        );
        this.isLoading = false;
      },
    );
  }

  terminateConnection(pin: MatchingPin): void {
    if (!pin || !pin.id) {
      return;
    }
    this.isLoading = true;
    this.benutzermanagementApi.terminateConnection(pin.id).subscribe(
      () => {
        this.toastService.show(
          'Erfolg',
          'Verbindung wurde aufgelöst',
          'success',
        );
        // Reload all account-related data since state changed
        this.loadAllAccountData();
      },
      (error) => {
        console.error('Error terminating connection', error);
        this.toastService.show(
          'Fehler',
          'Fehler beim Auflösen der Verbindung',
          'danger',
        );
        this.isLoading = false;
      },
    );
  }

  onTerminateManagedAccount(account: ManagedAccount): void {
    if (!account) {
      return;
    }

    const activePin = this.getActivePinForManagedAccount(account);
    if (activePin) {
      this.terminateConnection(activePin);
      return;
    }

    this.terminateManagedAccountRelation(account);
  }

  onTerminateManagerAccount(account: ManagedAccount): void {
    if (!account) {
      return;
    }

    const activePin = this.getActivePinForManagerAccount(account);
    if (activePin) {
      this.terminateConnection(activePin);
      return;
    }

    this.terminateManagerAccountRelation(account);
  }

  private terminateManagedAccountRelation(account: ManagedAccount): void {
    if (!account || account.id == null) {
      return;
    }

    this.isLoading = true;
    this.benutzermanagementApi
      .deleteManagedAccountRelation(account.id)
      .subscribe(
        () => {
          this.toastService.show(
            'Erfolg',
            'Verbindung wurde aufgelöst',
            'success',
          );
          this.loadAllAccountData();
        },
        (error) => {
          console.error('Error terminating managed account relation', error);
          this.toastService.show(
            'Fehler',
            'Fehler beim Auflösen der Verbindung',
            'danger',
          );
          this.isLoading = false;
        },
      );
  }

  private terminateManagerAccountRelation(account: ManagedAccount): void {
    if (!account || account.id == null) {
      return;
    }

    this.isLoading = true;
    this.benutzermanagementApi
      .deleteManagerAccountRelation(account.id)
      .subscribe(
        () => {
          this.toastService.show(
            'Erfolg',
            'Verbindung wurde aufgelöst',
            'success',
          );
          this.loadAllAccountData();
        },
        (error) => {
          console.error('Error terminating manager account relation', error);
          this.toastService.show(
            'Fehler',
            'Fehler beim Auflösen der Verbindung',
            'danger',
          );
          this.isLoading = false;
        },
      );
  }

  generatePin(): void {
    this.isLoading = true;
    this.benutzermanagementApi.generateMatchingPin().subscribe(
      (pin) => {
        this.currentPin = pin;
        this.isPinGenerated = true;

        // Refresh the matching pins list without toggling isLoading
        this.benutzermanagementApi.getMatchingPins().subscribe(
          (pins) => {
            this.matchingPins = pins;
            this.isLoading = false; // Finally set loading to false when all data is loaded
            this.managedAccountService.triggerReload();
          },
          (error) => {
            console.error('Error loading matching pins', error);
            // Don't show an error toast here as the PIN was generated successfully
            this.isLoading = false;
          },
        );
      },
      (error) => {
        console.error('Error generating PIN', error);
        this.toastService.show(
          'Fehler',
          'Fehler beim Generieren der PIN',
          'danger',
        );
        this.isLoading = false;
      },
    );
  }

  enterPin(): void {
    if (this.enterPinForm.invalid) {
      return;
    }

    const pin = String(this.enterPinForm.get('pin')?.value ?? '');
    this.isLoading = true;

    this.benutzermanagementApi.enterMatchingPin(pin).subscribe(
      (matchingPin) => {
        this.pendingConfirmation = matchingPin;
        this.isPinEntered = true;
        this.showConfirmation = true;

        // Reload all related data
        this.loadAllAccountData();
      },
      (error) => {
        console.error('Error entering PIN', error);
        this.toastService.show(
          'Fehler',
          'Fehler beim Eingeben der PIN. Bitte überprüfen Sie die PIN und versuchen Sie es erneut.',
          'danger',
        );
        this.isLoading = false;
      },
    );
  }

  confirmMatch(confirm: boolean): void {
    if (!this.pendingConfirmation || !this.pendingConfirmation.id) {
      return;
    }

    this.isLoading = true;
    this.benutzermanagementApi
      .confirmMatchingPin(this.pendingConfirmation.id, confirm)
      .subscribe(
        () => {
          if (confirm) {
            this.toastService.show(
              'Erfolg',
              'Verbindung erfolgreich hergestellt',
              'success',
            );
          } else {
            this.toastService.show(
              'Information',
              'Verbindung abgelehnt',
              'info',
            );
          }
          this.showConfirmation = false;
          this.pendingConfirmation = null;

          // Don't set isLoading to false here, as we're about to make more API calls
          // Instead, let the final API call set it to false

          // Reload all related data
          this.loadAllAccountData();
        },
        (error) => {
          console.error('Error confirming match', error);
          this.toastService.show(
            'Fehler',
            'Fehler bei der Bestätigung der Verbindung',
            'danger',
          );
          this.isLoading = false;
        },
      );
  }

  // Prototype flow: Manager confirms an already manager-confirmed PIN to activate it
  openManagerConfirmation(
    pin: MatchingPin,
    content: TemplateRef<unknown>,
  ): void {
    this.selectedPinForActivation = pin;
    this.modalService.open(content, { backdrop: 'static', centered: true });
  }

  openTerminateManagedAccount(
    account: ManagedAccount,
    content: TemplateRef<unknown>,
  ): void {
    this.selectedManagedAccountForTermination = account;
    this.selectedManagerAccountForTermination = null;
    this.selectedPinForTermination = null;
    this.modalService.open(content, { backdrop: 'static', centered: true });
  }

  openTerminateManagerAccount(
    account: ManagedAccount,
    content: TemplateRef<unknown>,
  ): void {
    this.selectedManagerAccountForTermination = account;
    this.selectedManagedAccountForTermination = null;
    this.selectedPinForTermination = null;
    this.modalService.open(content, { backdrop: 'static', centered: true });
  }

  openTerminateActivePin(
    pin: MatchingPin,
    content: TemplateRef<unknown>,
  ): void {
    this.selectedPinForTermination = pin;
    this.selectedManagedAccountForTermination = null;
    this.selectedManagerAccountForTermination = null;
    this.modalService.open(content, { backdrop: 'static', centered: true });
  }

  confirmTermination(modal: ModalRefLike): void {
    const pin = this.selectedPinForTermination;
    const managed = this.selectedManagedAccountForTermination;
    const manager = this.selectedManagerAccountForTermination;

    this.resetTerminationSelection();
    modal.close('confirm');

    if (pin) {
      this.terminateConnection(pin);
      return;
    }

    if (managed) {
      this.onTerminateManagedAccount(managed);
      return;
    }

    if (manager) {
      this.onTerminateManagerAccount(manager);
    }
  }

  cancelTermination(modal: ModalRefLike): void {
    this.resetTerminationSelection();
    modal.dismiss('cancel');
  }

  private resetTerminationSelection(): void {
    this.selectedPinForTermination = null;
    this.selectedManagedAccountForTermination = null;
    this.selectedManagerAccountForTermination = null;
  }

  confirmManagerActivation(): void {
    if (!this.selectedPinForActivation || !this.selectedPinForActivation.id) {
      return;
    }

    this.isLoading = true;
    const pinId = this.selectedPinForActivation.id;
    this.selectedPinForActivation = null;

    this.benutzermanagementApi.confirmMatchingPin(pinId, true).subscribe(
      () => {
        this.toastService.show(
          'Erfolg',
          'Freischaltung bestätigt (Prototyp)',
          'success',
        );

        // Reload data after confirming
        this.loadAllAccountData();
      },
      (error) => {
        console.error('Error confirming activation', error);
        this.toastService.show(
          'Fehler',
          'Fehler bei der Freischaltung',
          'danger',
        );
        this.isLoading = false;
      },
    );
  }

  // Prototype flow: Manager rejects an already manager-confirmed PIN
  openManagerRejection(pin: MatchingPin, content: TemplateRef<unknown>): void {
    this.selectedPinForRejection = pin;
    this.modalService.open(content, { backdrop: 'static', centered: true });
  }

  confirmManagerRejection(): void {
    if (!this.selectedPinForRejection || !this.selectedPinForRejection.id) {
      return;
    }

    this.isLoading = true;
    const pinId = this.selectedPinForRejection.id;
    this.selectedPinForRejection = null;

    this.benutzermanagementApi.confirmMatchingPin(pinId, false).subscribe(
      () => {
        this.toastService.show(
          'Abgelehnt',
          'Freischaltung wurde abgelehnt (Prototyp)',
          'warning',
        );

        // Reload data after rejecting
        this.loadAllAccountData();
      },
      (error) => {
        console.error('Error rejecting activation', error);
        this.toastService.show(
          'Fehler',
          'Fehler beim Ablehnen der Freischaltung',
          'danger',
        );
        this.isLoading = false;
      },
    );
  }

  copyPinToClipboard(): void {
    if (this.currentPin && this.currentPin.pin) {
      navigator.clipboard.writeText(this.currentPin.pin).then(
        () => {
          this.toastService.show(
            'Erfolg',
            'PIN in die Zwischenablage kopiert',
            'success',
          );
        },
        () => {
          this.toastService.show(
            'Fehler',
            'Fehler beim Kopieren der PIN',
            'danger',
          );
        },
      );
    }
  }

  resetPinGeneration(): void {
    this.isPinGenerated = false;
    this.currentPin = null;
  }

  resetPinEntry(): void {
    this.isPinEntered = false;
    this.enterPinForm.reset();
  }

  getStatusText(status: MatchingPinStatus | undefined): string {
    if (!status) {
      return 'Unbekannt';
    }

    switch (status) {
      case MatchingPinStatus.PENDING:
        return 'Ausstehend (PIN erstellt)';
      case MatchingPinStatus.CONFIRMED_BY_MANAGER:
        return 'Vom Verwalter bestätigt';
      case MatchingPinStatus.CONFIRMED_BY_USER:
        return 'PIN eingegeben';
      case MatchingPinStatus.ACTIVE:
        return 'Aktiv';
      case MatchingPinStatus.REJECTED:
        return 'Abgelehnt';
      default:
        return 'Unbekannt';
    }
  }

  getStatusClass(status: MatchingPinStatus | undefined): string {
    if (!status) {
      return '';
    }

    switch (status) {
      case MatchingPinStatus.PENDING:
        return 'text-warning';
      case MatchingPinStatus.CONFIRMED_BY_MANAGER:
      case MatchingPinStatus.CONFIRMED_BY_USER:
        return 'text-info';
      case MatchingPinStatus.ACTIVE:
        return 'text-success';
      case MatchingPinStatus.REJECTED:
        return 'text-danger';
      default:
        return '';
    }
  }

  canCurrentUserConfirm(pin: MatchingPin): boolean {
    if (!pin) {
      return false;
    }

    const isTrustingUser =
      pin.user_info && this.isCurrentUserInfo(pin.user_info);
    const isTrustedUser =
      pin.manager_info && this.isCurrentUserInfo(pin.manager_info);

    // Vom Verwalter bestätigt -> nur Verwalteter (trusting user) sieht Buttons
    if (
      pin.status === MatchingPinStatus.CONFIRMED_BY_MANAGER &&
      isTrustingUser
    ) {
      return true;
    }

    // PIN eingegeben -> nur Verwalter (trusted user) sieht Buttons
    if (pin.status === MatchingPinStatus.CONFIRMED_BY_USER && isTrustedUser) {
      return true;
    }

    return false;
  }

  getCounterpartyUser(pin: MatchingPin | null): ManagedAccount | undefined {
    if (!pin) {
      return undefined;
    }

    const isTrustingUser =
      pin.user_info && this.isCurrentUserInfo(pin.user_info);
    const isTrustedUser =
      pin.manager_info && this.isCurrentUserInfo(pin.manager_info);

    if (isTrustingUser) {
      return pin.manager_info;
    }

    if (isTrustedUser) {
      return pin.user_info;
    }

    // Fallback: prefer trusting user details, otherwise trusted user
    return pin.user_info || pin.manager_info;
  }

  isCurrentUserInfo(user: ManagedAccount | undefined): boolean {
    if (!user) {
      return false;
    }
    return this.currentUsername === user.kundennummer;
  }

  private isSameAccount(
    a: ManagedAccount | undefined,
    b: ManagedAccount | undefined,
  ): boolean {
    if (!a || !b) {
      return false;
    }

    if (a.uuid && b.uuid && a.uuid === b.uuid) {
      return true;
    }

    return !!(
      a.kundennummer &&
      b.kundennummer &&
      a.kundennummer === b.kundennummer
    );
  }

  getActivePinForManagedAccount(
    account: ManagedAccount,
  ): MatchingPin | undefined {
    return this.matchingPins.find(
      (pin) =>
        pin.status === MatchingPinStatus.ACTIVE &&
        pin.user_info &&
        pin.manager_info &&
        this.isSameAccount(pin.user_info, account) &&
        this.isCurrentUserInfo(pin.manager_info),
    );
  }

  getActivePinForManagerAccount(
    account: ManagedAccount,
  ): MatchingPin | undefined {
    return this.matchingPins.find(
      (pin) =>
        pin.status === MatchingPinStatus.ACTIVE &&
        pin.user_info &&
        pin.manager_info &&
        this.isSameAccount(pin.manager_info, account) &&
        this.isCurrentUserInfo(pin.user_info),
    );
  }
}
